/**
 * useAIAssistant — React Hook for the Lina AI Assistant
 *
 * Manages all AI assistant state and actions:
 *  - Message list (user + assistant messages, action cards)
 *  - Streaming state (isLoading, isStreaming)
 *  - Conversation management (list, switch, new, delete)
 *  - Action confirmation flow (confirm / cancel destructive actions)
 *  - Context awareness (current page, workspace, project)
 *  - Error handling with auto-dismiss
 *
 * @module hooks/useAIAssistant
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import aiService from '../services/aiService';
import fileService from '../services/fileService';
import { useAuth } from '../context/AuthContext';
import { useMode } from '../context/ModeContext';
import { useOrganization } from '../context/OrganizationContext';
import { emitUiRefreshFromAiAction } from '../utils/aiActionRefresh';

// ── Message ID Generator ─────────────────────────────────────────────────────

let _msgSeq = 0;
function nextMsgId(prefix = 'msg') {
  return `${prefix}_${Date.now()}_${++_msgSeq}`;
}

// ── Navigation path extractor (fallback for when LLM doesn't call navigate) ──

/**
 * Extract an internal navigation path from AI response text.
 * Looks for markdown links like [Go to Tasks](/tasks) and returns the path.
 * Only returns paths that start with / and look like valid app routes.
 */
function extractNavigationPath(text) {
  if (!text) return null;
  // Match markdown links with internal paths: [any text](/some/path)
  const linkRegex = /\[.*?\]\((\/[a-z][a-z0-9/\-]*)\)/gi;
  const match = linkRegex.exec(text);
  if (match) return match[1];
  return null;
}

// ── Page-Context Suggestions ─────────────────────────────────────────────────
// Minimal client-side fallback. The backend SuggestionEngine provides
// richer context-aware suggestions via GET /ai/suggestions.

const DEFAULT_SUGGESTIONS = [
  { label: 'What can you help me with?', prompt: 'What can you help me with?' },
  { label: 'Show my tasks', prompt: 'Show my tasks' },
  { label: 'Create a new task', prompt: 'Create a new task' },
];

/**
 * Fetch backend-powered suggestions. Falls back to defaults on error.
 * @param {Object} context - { currentPage, workspace, lastAction, projectId }
 */
async function fetchSuggestions(context) {
  try {
    const backendSuggestions = await aiService.getSuggestions(context);
    // Backend returns { label, prompt, icon } — normalize to { label, prompt }
    return backendSuggestions.map((s) => ({
      label: s.label || s.prompt,
      prompt: s.prompt || s.label,
    }));
  } catch {
    return DEFAULT_SUGGESTIONS;
  }
}

// ── Extract Page Context ─────────────────────────────────────────────────────

function extractPageContext(pathname, currentUser, currentMode, currentOrganization) {
  const segments = pathname.split('/').filter(Boolean);
  let currentPage = segments[0] || 'dashboard';

  // Detect project context from URL
  const projectMatch = pathname.match(/\/projects\/([a-f\d]{24})/);

  // Detect org workspace via URL patterns OR ModeContext's currentMode
  const isOrgWorkspace =
    pathname.startsWith('/org/') ||
    pathname === '/org' ||
    currentMode === 'organization';

  // For org workspace, the current page is the segment after /org/
  if (isOrgWorkspace) {
    currentPage = segments[1] || 'dashboard';
  }

  const workspace = isOrgWorkspace ? 'organization' : 'personal';
  const organizationId = currentOrganization?._id || currentOrganization?.id || currentUser?.organizationId || null;

  return {
    currentPage,
    projectId: projectMatch?.[1] || null,
    organizationId,
    workspace,
    // In org workspace, workspaceId = organizationId for backend scoping
    workspaceId: (workspace === 'organization' && organizationId) ? organizationId : null,
  };
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useAIAssistant() {
  // ── Auth Context ───────────────────────────────────────────────────────────

  const { currentUser } = useAuth();
  const { currentMode } = useMode();
  const { currentOrganization } = useOrganization();

  // ── State ──────────────────────────────────────────────────────────────────

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [statusText, setStatusText] = useState(null);
  const [lastAction, setLastAction] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const streamingMsgRef = useRef(null);
  const previousWorkspaceKeyRef = useRef(null);

  // ── Token drip buffer for smooth character-by-character streaming ──
  const tokenBufferRef = useRef('');
  const dripTimerRef = useRef(null);
  const DRIP_INTERVAL = 6; // ms between drip ticks (was 12)
  const DRIP_SIZE = 8; // characters per tick (was 3)

  /** Start the drip timer that gradually moves characters from buffer → message */
  const startDrip = useCallback(() => {
    if (dripTimerRef.current) return;
    dripTimerRef.current = setInterval(() => {
      if (tokenBufferRef.current.length === 0) {
        clearInterval(dripTimerRef.current);
        dripTimerRef.current = null;
        return;
      }
      // Pull a small chunk from the pending buffer
      const size = Math.min(DRIP_SIZE, tokenBufferRef.current.length);
      const chars = tokenBufferRef.current.slice(0, size);
      tokenBufferRef.current = tokenBufferRef.current.slice(size);
      const targetId = streamingMsgRef.current;
      if (targetId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === targetId
              ? { ...msg, content: msg.content + chars, statusText: null }
              : msg
          )
        );
      }
    }, DRIP_INTERVAL);
  }, []);

  /** Flush any remaining buffer characters immediately */
  const flushDrip = useCallback(() => {
    if (dripTimerRef.current) {
      clearInterval(dripTimerRef.current);
      dripTimerRef.current = null;
    }
    const remaining = tokenBufferRef.current;
    tokenBufferRef.current = '';
    return remaining;
  }, []);

  const stopActiveStream = useCallback(() => {
    aiService.cancelStream();
    const remaining = flushDrip();
    streamingMsgRef.current = null;
    setIsLoading(false);
    setIsStreaming(false);
    setStatusText(null);
    return remaining;
  }, [flushDrip]);

  // ── Derived Context ────────────────────────────────────────────────────────

  const pageContext = useMemo(
    () => extractPageContext(location.pathname, currentUser, currentMode, currentOrganization),
    [location.pathname, currentUser, currentMode, currentOrganization]
  );
  const workspaceKey = `${pageContext.workspace}:${pageContext.workspaceId || 'personal'}`;

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!previousWorkspaceKeyRef.current) {
      previousWorkspaceKeyRef.current = workspaceKey;
      return;
    }

    if (previousWorkspaceKeyRef.current !== workspaceKey) {
      stopActiveStream();
      setMessages([]);
      setConversationId(null);
      setConversations([]);
      setError(null);
      setLastAction(null);
      setStatusText(null);
      previousWorkspaceKeyRef.current = workspaceKey;
    }
  }, [workspaceKey, stopActiveStream]);

  // Fetch starter suggestions when page changes (for empty state) — debounced
  const suggestDebounceRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    // Debounce: wait 300ms after last dependency change before fetching
    clearTimeout(suggestDebounceRef.current);
    suggestDebounceRef.current = setTimeout(() => {
      fetchSuggestions({
        currentPage: pageContext.currentPage,
        workspace: pageContext.workspace,
        lastAction: lastAction || undefined,
        projectId: pageContext.projectId || undefined,
      }).then((s) => {
        if (!cancelled) setSuggestions(s);
      });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(suggestDebounceRef.current);
    };
  }, [location.pathname, lastAction, pageContext.currentPage, pageContext.workspace, pageContext.projectId]);

  // Auto-dismiss errors after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Keyboard shortcut: Cmd+K / Ctrl+K to toggle
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Cleanup drip timer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (dripTimerRef.current) {
        clearInterval(dripTimerRef.current);
        dripTimerRef.current = null;
      }
      tokenBufferRef.current = '';
    };
  }, []);

  // ── Actions ────────────────────────────────────────────────────────────────

  /**
   * Send a message to the AI assistant (streaming).
   * Falls back to non-streaming if SSE fails.
   * @param {string} text - Message text
   * @param {object} [options] - Optional: { inputType: 'text'|'voice' }
   */
  const sendMessage = useCallback(async (text, options = {}) => {
    if ((!text?.trim() && (!options.files || options.files.length === 0)) || isLoading || isStreaming) return;

    const inputType = options.inputType || 'text';
    const filesToUpload = Array.isArray(options.files) ? options.files : [];

    // Upload any attached files first so the AI receives their fileIds
    let uploadedFiles = [];
    if (filesToUpload.length > 0) {
      setIsLoading(true);
      try {
        const orgId = pageContext.workspace === 'organization' ? pageContext.organizationId : null;
        uploadedFiles = await Promise.all(
          filesToUpload.map(async (file) => {
            const ctx = { type: 'general' }; // will be re-tagged when AI links it to a task
            const uploaded = orgId
              ? await fileService.uploadOrganizationFile(orgId, file, { context: ctx })
              : await fileService.uploadPersonalFile(file, { context: ctx });
            return {
              fileId: uploaded?._id || uploaded?.id || uploaded?.fileId,
              name: file.name,
              size: file.size,
              mimeType: file.type,
            };
          })
        );
      } catch (uploadErr) {
        setIsLoading(false);
        const serverMsg = uploadErr?.response?.data?.message
          || uploadErr?.response?.data?.error
          || uploadErr?.message
          || 'Unknown error';
        toast.error(`File upload failed: ${serverMsg}`);
        return;
      }
    }

    // Build the visible user message text. The AI receives ONLY this text
    // — no fileId injection. Attachment IDs are sent out-of-band so the
    // orchestrator can auto-link them to whatever entity the AI creates
    // (task / project / comment / discussion).
    const visibleText = text?.trim() || (uploadedFiles.length > 0
      ? (uploadedFiles.length === 1
          ? `Attached ${uploadedFiles[0].name}`
          : `Attached ${uploadedFiles.length} files`)
      : '');

    const messageText = visibleText;
    const attachmentIds = uploadedFiles.map((f) => f.fileId).filter(Boolean);

    const userMsg = {
      id: nextMsgId('user'),
      role: 'user',
      content: visibleText,
      timestamp: new Date(),
      inputType,
      attachments: uploadedFiles.length > 0 ? uploadedFiles : undefined,
    };

    // Clear follow-ups from the last assistant message only (older follow-ups are historical)
    setMessages((prev) => {
      // Find last assistant message index that has followUps
      let lastFollowUpIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].role === 'assistant' && prev[i].followUps) {
          lastFollowUpIdx = i;
          break;
        }
      }
      const cleaned = lastFollowUpIdx >= 0
        ? prev.map((msg, i) => i === lastFollowUpIdx ? { ...msg, followUps: undefined } : msg)
        : prev;
      return [...cleaned, userMsg];
    });
    setIsLoading(true);
    setIsStreaming(true);
    setError(null);

    // Create a placeholder assistant message for streaming
    const assistantMsgId = nextMsgId('ai');
    const assistantMsg = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages((prev) => [...prev, assistantMsg]);
    streamingMsgRef.current = assistantMsgId;

    const context = {
      workspace: pageContext.workspace,
      workspaceId: pageContext.workspaceId,
      currentPage: pageContext.currentPage,
      projectId: pageContext.projectId,
      inputType,
    };

    try {
      let accumulatedText = ''; // Track streamed text for navigation fallback
      await aiService.sendMessageStream(
        { message: messageText, conversationId, context, attachmentIds, attachments: uploadedFiles },
        {
          onStart: () => {
            setIsLoading(false); // Loading complete, now actively streaming
          },
          onStatus: (statusEvent) => {
            // Step events from the orchestrator describe what Lina is doing
            // right now (understanding, searching, planning, executing an
            // action, generating, etc.). Keep the latest as `statusText` for
            // the header pulse, AND accumulate the full sequence on the
            // assistant message so the UI can render a step-by-step timeline.
            const stepKey = statusEvent.step || 'thinking';
            const stepText = statusEvent.text;
            // `done` is a sentinel that just clears the status — don't add it.
            if (stepKey === 'done' || !stepText) {
              setStatusText(null);
              setMessages((prev) =>
                prev.map((msg) => {
                  if (msg.id !== assistantMsgId) return msg;
                  // Mark the most recent in-progress step as completed
                  const steps = (msg.steps || []).map((s, i, arr) =>
                    i === arr.length - 1 && s.status === 'active'
                      ? { ...s, status: 'done', endedAt: Date.now() }
                      : s
                  );
                  return { ...msg, statusText: null, steps };
                })
              );
              return;
            }
            setStatusText(stepText);
            setMessages((prev) =>
              prev.map((msg) => {
                if (msg.id !== assistantMsgId) return msg;
                const prevSteps = msg.steps || [];
                const last = prevSteps[prevSteps.length - 1];
                // Mark previous active step as done before appending the new one
                const closed = prevSteps.map((s, i) =>
                  i === prevSteps.length - 1 && s.status === 'active'
                    ? { ...s, status: 'done', endedAt: Date.now() }
                    : s
                );
                // Avoid duplicating a status that just arrived twice in a row
                if (last && last.key === stepKey && last.text === stepText && last.status === 'active') {
                  return { ...msg, statusText: stepText };
                }
                return {
                  ...msg,
                  statusText: stepText,
                  steps: [
                    ...closed,
                    { id: `${stepKey}-${Date.now()}`, key: stepKey, text: stepText, status: 'active', startedAt: Date.now() },
                  ],
                };
              })
            );
          },
          onToken: (token) => {
            // Buffer tokens and drip them for smooth character-by-character appearance
            accumulatedText += token;
            setStatusText(null);
            // First token = response started flowing — close any active step.
            if (tokenBufferRef.current.length === 0) {
              setMessages((prev) =>
                prev.map((msg) => {
                  if (msg.id !== assistantMsgId) return msg;
                  const steps = (msg.steps || []).map((s, i, arr) =>
                    i === arr.length - 1 && s.status === 'active'
                      ? { ...s, status: 'done', endedAt: Date.now() }
                      : s
                  );
                  return { ...msg, statusText: null, steps };
                })
              );
            }
            tokenBufferRef.current += token;
            startDrip();
          },
          onAction: (actionEvent) => {
            const isWorkspaceSwitchAction = actionEvent.action === 'navigate_switchWorkspace'
              || actionEvent.action === 'navigate.switchWorkspace';

            // Handle confirmation-required actions — show confirmation card, don't navigate
            if (actionEvent.result?.requiresConfirmation) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        action: {
                          name: actionEvent.result.actionName || actionEvent.action,
                          actionId: actionEvent.action,
                          result: actionEvent.result,
                          pendingId: actionEvent.result.pendingId,
                          requiresConfirmation: true,
                        },
                      }
                    : msg
                )
              );
              return; // Don't process navigation or theme for unconfirmed actions
            }

            // Handle navigation actions — actually navigate the user
            if (actionEvent.result?.navigation?.path) {
              const remaining = isWorkspaceSwitchAction ? stopActiveStream() : '';
              navigate(actionEvent.result.navigation.path);
              // Clear status text immediately — don't keep "Navigating…" while
              // waiting for the LLM text response
              setStatusText(null);
              setMessages((prev) =>
                prev.map((msg) => {
                  if (msg.id !== assistantMsgId) return msg;
                  const steps = (msg.steps || []).map((s) =>
                    s.status === 'active' ? { ...s, status: 'done', endedAt: Date.now() } : s
                  );
                  const content = isWorkspaceSwitchAction
                    ? (`${msg.content || ''}${remaining || ''}` || actionEvent.result.message || '')
                    : msg.content;
                  return {
                    ...msg,
                    content,
                    isStreaming: isWorkspaceSwitchAction ? false : msg.isStreaming,
                    statusText: null,
                    steps,
                  };
                })
              );
            }
            // Handle theme change actions — dispatch event for ThemeContext
            if (actionEvent.result?.themeChange) {
              window.dispatchEvent(new CustomEvent('ai-theme-change', {
                detail: actionEvent.result.themeChange,
              }));
            }
            // Track last action for follow-up suggestions
            if (actionEvent.action) {
              setLastAction(actionEvent.action);
            }
            // Notify the rest of the app that data changed so any open view
            // (Messages, Tasks, Projects…) can refetch without a manual reload.
            if (actionEvent.action && actionEvent.result?.success !== false) {
              window.dispatchEvent(new CustomEvent('ai:action-executed', {
                detail: { action: actionEvent.action, result: actionEvent.result },
              }));
              if (
                actionEvent.action === 'communication.sendDirectMessage'
                && actionEvent.result?.data?.conversationId
              ) {
                window.dispatchEvent(new CustomEvent('chat:open-conversation', {
                  detail: { conversationId: actionEvent.result.data.conversationId },
                }));
              }
              emitUiRefreshFromAiAction(actionEvent.action, actionEvent.result);
            }
            // An action was executed — attach the result to the assistant message
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMsgId
                  ? { ...msg, action: { name: actionEvent.action, result: actionEvent.result } }
                  : msg
              )
            );
          },
          onDone: (result) => {
            setStatusText(null);
            // Flush any remaining drip buffer characters immediately
            const remaining = flushDrip();

            // Handle navigation in non-streaming action result
            if (result.action?.result?.navigation?.path) {
              navigate(result.action.result.navigation.path);
            }
            if (
              result.action?.action === 'communication.sendDirectMessage'
              && result.action?.result?.data?.conversationId
            ) {
              window.dispatchEvent(new CustomEvent('chat:open-conversation', {
                detail: { conversationId: result.action.result.data.conversationId },
              }));
            } else if (
              // Handle theme change in non-streaming result
              result.action?.result?.themeChange
            ) {
              window.dispatchEvent(new CustomEvent('ai-theme-change', {
                detail: result.action.result.themeChange,
              }));
            } else if (
              // Only try text-based navigation extraction when no action was executed.
              // When the planner (workflow.plan) or direct navigate actions ran,
              // the real-time onAction callbacks already handled navigation.
              !result.action?.action
            ) {
              // Final fallback: extract internal link from response text when LLM
              // didn't call navigate function but included a markdown link
              const navPath = extractNavigationPath(accumulatedText);
              if (navPath) {
                navigate(navPath);
              }
            }
            setMessages((prev) =>
              prev.map((msg) => {
                if (msg.id !== assistantMsgId) return msg;
                // Mark every step as completed at the end of the run
                const steps = (msg.steps || []).map((s) =>
                  s.status === 'active' ? { ...s, status: 'done', endedAt: Date.now() } : s
                );
                return {
                  ...msg,
                  content: msg.content + remaining,
                  isStreaming: false,
                  statusText: null,
                  steps,
                  ...(result.action ? { action: { name: result.action.action, result: result.action.result } } : {}),
                };
              })
            );
            if (result.conversationId) {
              setConversationId(result.conversationId);
            }
            // Attach follow-up suggestions to this assistant message (inline below response)
            if (result.suggestions?.length > 0) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMsgId
                    ? { ...msg, followUps: result.suggestions }
                    : msg
                )
              );
            }
            setIsStreaming(false);
            streamingMsgRef.current = null;
          },
          onError: (errorEvent) => {
            // Clean up drip buffer on error
            flushDrip();
            // Fall back to non-streaming
            handleStreamError(errorEvent, messageText, context, assistantMsgId);
          },
        }
      );
    } catch (err) {
      handleStreamError(err, messageText, context, assistantMsgId);
    }
  }, [conversationId, isLoading, isStreaming, pageContext, navigate, startDrip, flushDrip, stopActiveStream]);

  /**
   * Handle streaming failure — fall back to non-streaming chat.
   */
  const handleStreamError = useCallback(async (streamErr, messageText, context, assistantMsgId) => {
    try {
      const result = await aiService.sendMessage({
        message: messageText,
        conversationId,
        context,
      });

      const responseContent = result.response?.text || 'I received your message but couldn\'t generate a response.';
      const actionResult = result.response?.action || null;

      // Handle navigation in fallback non-streaming result
      if (actionResult?.result?.navigation?.path) {
        navigate(actionResult.result.navigation.path);
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMsgId
            ? {
                ...msg,
                content: responseContent,
                isStreaming: false,
                action: actionResult,
              }
            : msg
        )
      );

      if (result.conversationId) {
        setConversationId(result.conversationId);
      }
    } catch (fallbackErr) {
      // Check for billing limit errors (plan/feature/usage limits)
      const isBilling = fallbackErr.isBillingLimit
        || fallbackErr.response?.data?.code === 'USAGE_LIMIT_REACHED'
        || fallbackErr.response?.data?.code === 'FEATURE_UNAVAILABLE';

      const errorMessage = isBilling
        ? (fallbackErr.billingMessage || fallbackErr.response?.data?.error || 'You have reached your AI usage limit. Upgrade your plan for more queries.')
        : (fallbackErr.response?.data?.error || fallbackErr.message || 'Something went wrong. Please try again.');

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMsgId
            ? {
                ...msg,
                content: errorMessage,
                isStreaming: false,
                isError: true,
                isBillingLimit: isBilling,
              }
            : msg
        )
      );
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setStatusText(null);
      streamingMsgRef.current = null;
    }
  }, [conversationId, navigate]);

  /**
   * Confirm a pending destructive action.
   */
  const confirmAction = useCallback(async (pendingId, messageId) => {
    try {
      setIsLoading(true);
      const response = await aiService.confirmAction(pendingId, conversationId);
      // The controller replies { confirmed, result } where `result` is the
      // action handler's own { success, data, message }. Unwrap it so we can
      // surface the real confirmation text instead of a generic "done".
      const actionResult = response?.result || response || {};
      const resultMessage =
        actionResult?.message ||
        actionResult?.result?.message ||
        'Done - your request was completed.';

      const sourceMessage = messages.find((msg) => msg.id === messageId);
      const confirmedActionId =
        sourceMessage?.action?.actionId || actionResult?.actionId || null;

      // 1) Update the confirmation card to its executed state and carry the
      //    real result message through for display.
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id !== messageId) return msg;
          return {
            ...msg,
            action: {
              ...msg.action,
              confirmed: true,
              result: {
                ...(msg.action?.result || {}),
                confirmed: true,
                message: resultMessage,
                data: actionResult?.data,
              },
            },
          };
        })
      );

      // 2) Add a fresh assistant bubble so the user sees the AI's actual
      //    confirmation response in the conversation (not just the card).
      setMessages((prev) => [
        ...prev,
        {
          id: `ai-confirm-${Date.now()}`,
          role: 'assistant',
          content: resultMessage,
          isStreaming: false,
        },
      ]);

      // 3) Tell the rest of the app data changed so the affected view refreshes.
      window.dispatchEvent(new CustomEvent('ai:action-executed', {
        detail: { action: confirmedActionId, result: actionResult },
      }));
      emitUiRefreshFromAiAction(confirmedActionId, actionResult);

      toast.success(resultMessage);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to confirm action.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, messages]);

  /**
   * Cancel a pending destructive action.
   */
  const cancelAction = useCallback(async (pendingId, messageId) => {
    try {
      await aiService.cancelAction(pendingId);

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                action: {
                  ...msg.action,
                  cancelled: true,
                },
              }
            : msg
        )
      );
      toast.info('Action cancelled.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel action.');
    }
  }, []);

  /**
   * Load conversation history.
   */
  const loadConversations = useCallback(async () => {
    try {
      const result = await aiService.listConversations({ context: pageContext });
      setConversations(result.conversations || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    }
  }, [pageContext]);

  /**
   * Switch to a different conversation.
   */
  const switchConversation = useCallback(async (id) => {
    try {
      setIsLoading(true);
      const result = await aiService.getConversation(id, { context: pageContext });
      const conv = result.conversation;

      if (conv?.messages) {
        const formatted = conv.messages
          // Only display user and assistant messages — filter out internal
          // protocol messages (function, model_function_call, system)
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .map((m, i, arr) => {
            // Only attach follow-up suggestions to the LAST assistant message
            const isLastAssistant = m.role === 'assistant' &&
              !arr.slice(i + 1).some(later => later.role === 'assistant');
            return {
              id: nextMsgId(m.role === 'user' ? 'user' : 'ai'),
              role: m.role === 'user' ? 'user' : 'assistant',
              content: m.content,
              timestamp: new Date(m.timestamp || m.createdAt),
              ...(m.role === 'user' && Array.isArray(m.attachments) && m.attachments.length > 0
                ? { attachments: m.attachments }
                : {}),
              ...(isLastAssistant && m.suggestions?.length > 0 ? { followUps: m.suggestions } : {}),
            };
          });
        setMessages(formatted);
      }
      setConversationId(id);
    } catch (err) {
      setError('Failed to load conversation.');
    } finally {
      setIsLoading(false);
    }
  }, [pageContext]);

  /**
   * Start a new conversation.
   */
  const newConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    aiService.cancelStream();
  }, []);

  /**
   * Delete a conversation.
   */
  const deleteConversation = useCallback(async (id) => {
    try {
      await aiService.deleteConversation(id, { context: pageContext });
      setConversations((prev) => prev.filter((c) => c._id !== id));
      if (conversationId === id) {
        newConversation();
      }
      toast.success('Conversation deleted.');
    } catch (err) {
      toast.error('Failed to delete conversation.');
    }
  }, [conversationId, newConversation, pageContext]);

  /**
   * Use a suggestion chip. Accepts either a string or { label, prompt } object.
   */
  const useSuggestion = useCallback((suggestion) => {
    const text = typeof suggestion === 'string' ? suggestion : suggestion?.prompt || suggestion?.label;
    if (text) sendMessage(text);
  }, [sendMessage]);

  /**
   * Submit thumbs-up / thumbs-down feedback on an AI message.
   * @param {string} targetConversationId - Conversation ID
   * @param {number} messageIndex - Index of the message in the conversation
   * @param {'positive'|'negative'} rating
   */
  const submitFeedback = useCallback(async (targetConversationId, messageIndex, rating) => {
    if (!targetConversationId || messageIndex == null || !rating) return;
    try {
      await aiService.submitFeedback({
        conversationId: targetConversationId,
        messageIndex,
        rating,
      });
    } catch {
      // Feedback is fire-and-forget — don't surface errors to user
    }
  }, []);

  /**
   * Toggle the assistant panel open/closed.
   */
  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  /**
   * Close the assistant panel.
   */
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Clear all messages.
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    aiService.cancelStream();
  }, []);

  /**
   * Stop the current streaming response.
   * Aborts the fetch, flushes buffered tokens, and finalizes the message.
   */
  const stopStreaming = useCallback(() => {
    if (!isStreaming && !isLoading) return;

    aiService.cancelStream();

    // Flush remaining buffered tokens into the message
    const remaining = flushDrip();
    const targetId = streamingMsgRef.current;

    if (targetId) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === targetId
            ? { ...msg, content: msg.content + remaining, isStreaming: false, statusText: null }
            : msg
        )
      );
    }

    setIsLoading(false);
    setIsStreaming(false);
    setStatusText(null);
    streamingMsgRef.current = null;
  }, [isStreaming, isLoading, flushDrip]);

  // ── Return ─────────────────────────────────────────────────────────────────

  return {
    // State
    messages,
    isLoading,
    isStreaming,
    isOpen,
    error,
    conversationId,
    conversations,
    suggestions,
    statusText,
    messagesEndRef,

    // Actions
    sendMessage,
    stopStreaming,
    confirmAction,
    cancelAction,
    loadConversations,
    switchConversation,
    newConversation,
    deleteConversation,
    useSuggestion,
    submitFeedback,
    toggleOpen,
    close,
    clearMessages,
    setError,
  };
}

export default useAIAssistant;
